import {
  defer,
  distinctUntilChanged,
  fromEvent,
  map,
  merge,
  mergeWith,
  type Observable,
  of,
} from 'rxjs'
import { share } from 'rxjs/operators'

/**
 * Describes the activity status of the current page.
 */
export interface PageActivityDescriptor {
  /**
   * Whether the page is visible - that is, the tab is the currently visible
   * one in the browser.
   */
  visible: boolean
  /**
   * Whether the page has focus - the page can be visible but not have focus.
   */
  focused: boolean
}

export const PageActivityDescriptor = {
  /**
   * Determines if 2 page activity descriptors are equal.
   *
   * @param left
   * @param right
   */
  equals: (left: PageActivityDescriptor, right: PageActivityDescriptor) =>
    left.visible === right.visible && left.focused === right.focused,
}

// Shared page activity stream so we only keep a single set of event listeners.
const sharedPageActivity$ = defer(() =>
  merge(
    fromEvent(document, 'visibilitychange'),
    fromEvent(window, 'focus'),
    fromEvent(window, 'blur'),
  ),
).pipe(map(readPageActivity), share())

/**
 * Returns a stream of page activity updates.
 *
 * @param fireImmediately If `true`, reads the page activity status immediately
 * on subscribe. Default `false`.
 */
export function pageActivityStream(
  fireImmediately?: boolean,
): Observable<PageActivityDescriptor> {
  let stream$ = sharedPageActivity$

  if (fireImmediately) {
    stream$ = stream$.pipe(mergeWith(defer(() => of(readPageActivity()))))
  }

  return stream$.pipe(distinctUntilChanged(PageActivityDescriptor.equals))
}

/**
 * Reads the current page activity status.
 */
export function readPageActivity(): PageActivityDescriptor {
  return {
    focused: document.hasFocus(),
    visible: document.visibilityState === 'visible',
  }
}
